import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { RecoveryInfo } from 'mbs-ui-shared@models/backup/recovery-info.interface';
import {
  GetBackupContentBunchType,
  GetBackupContentType,
  ItemTypeEnum,
  MyTreeElements,
  StorageEncryptionState,
  TreeBunchesIcons,
  TreeRestorePointsIconPath
} from 'mbs-ui-shared@models/backup/storages-type';
import Computer from 'mbs-ui-shared@models/Computer';
import { mediumDateWithTimeMoment } from 'mbs-ui-shared@utils/date';
import moment from 'moment/moment';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { ArchiveRestorePointSummaryStatus, IdsForCurrentFormats } from 'wizards/models/backup-to-restore-models';
import { TreeIconPath } from 'wizards/models/what-backup-tree-model';

type DataFromItemType = { src: string; label: string; rightBottomIcon: string; leftTopIcon: string; restorePoint?: string };
export type DataForAddComputerType = { abstractData?: any; computer?: Computer; storage?: string };

@Injectable({ providedIn: 'root' })
export class BackupStorageService {
  private i18nPipe = inject(I18NextPipe);

  public allowedRecovery: WritableSignal<boolean> = signal<boolean>(false);
  public needHardReset: WritableSignal<boolean> = signal<boolean>(false);
  public password: WritableSignal<{ [key: string]: string }> = signal<{ [key: string]: string }>({});
  public searchResult: WritableSignal<MyTreeElements[]> = signal<MyTreeElements[]>([]);
  public searchString: WritableSignal<string> = signal<string>('');
  public selectedItems: WritableSignal<MyTreeElements[]> = signal<MyTreeElements[]>([]);
  public canRVAndDiff: WritableSignal<boolean> = signal(null);

  public dataForAddComputer: WritableSignal<DataForAddComputerType> = signal(null);
  public dataForAddAbstract: WritableSignal<DataForAddComputerType> = signal(null);

  private readonly quickRestoreTypes = [
    ItemTypeEnum.Bunch,
    ItemTypeEnum.Generation,
    ItemTypeEnum.RestorePoint,
    ItemTypeEnum.RestorePointIncremental,
    ItemTypeEnum.RestorePointTLog
  ];
  private readonly SQLTypes: GetBackupContentBunchType[] = [
    GetBackupContentBunchType.MsSql,
    GetBackupContentBunchType.Database,
    GetBackupContentBunchType.Exchange
  ];
  private readonly restorePointTypes = [ItemTypeEnum.RestorePoint, ItemTypeEnum.RestorePointIncremental, ItemTypeEnum.RestorePointTLog];

  public joinForInput = '\\';
  public providerTimezone = 0;

  public resetData(): void {
    this.password.set({});
    this.searchResult.set([]);
    this.searchString.set('');
    this.selectedItems.set([]);
    this.canRVAndDiff.set(null);
  }

  public getCorrectProgress(data: RecoveryInfo): string {
    if (!data || isNil(data.stats)) return null;

    const progress = data?.stats?.progress;

    if (isNil(progress) || String(progress).startsWith('-') || +progress > 100) return '-1';

    return String(progress).trim();
  }

  public getDiskSizeFromKB(bytes: number, i18nPipe: I18NextPipe): string {
    if (bytes < 1024) return `${bytes} ${i18nPipe.transform('shared:sizes.bytes').toUpperCase()}`;

    const kb = +(bytes / 1024).toFixed(1);

    if (kb < 1024) return `${kb} ${i18nPipe.transform('shared:sizes.kb').toUpperCase()}`;

    const mb = +(kb / 1024).toFixed(1);

    if (mb < 1024) return `${mb} ${i18nPipe.transform('shared:sizes.mb').toUpperCase()}`;

    return `${+(mb / 1024).toFixed(1)} ${i18nPipe.transform('shared:sizes.gb').toUpperCase()}`;
  }

  private someParentIsSql(root: MyTreeElements): boolean {
    return this.SQLTypes.includes(root.type as GetBackupContentBunchType) || (!!root.parent && this.someParentIsSql(root.parent));
  }

  public getNewTreeItemsFromBackupContent(
    data: { items: any[]; isCanBeQuickRestored: boolean; restorePointObjectPath: string },
    root: MyTreeElements,
    storageId: string,
    online = true
  ): MyTreeElements[] {
    if (!data?.items?.length) return [];

    const keys = Object.keys(IdsForCurrentFormats);

    const someParentIsSQL: boolean = this.someParentIsSql(root) || this.someParentIsSql(data.items[0]);
    const isRestorePoints: boolean = this.restorePointTypes.includes(data.items[0]?.itemType);
    const isDeep = root.itemType !== ItemTypeEnum.Generation && root.itemType !== ItemTypeEnum.Bunch;

    return data.items.map((item: any, idx: number) => {
      const itemData = this.getDataFromItemType(item, root);
      const isLegacy = this.isLegacy(item, root, keys);

      const treeElement = {
        prefix: root.prefix,
        label: itemData.label,
        retentionKeepBackupFor: item.retentionKeepBackupFor,
        id: item.path + idx,
        gotChildren: (item.itemType === ItemTypeEnum.Version || (item.isCanBeExpand === false && online)) && !root?.isAbstract,
        shown: item.isCanBeExpand || (item.itemType !== ItemTypeEnum.File && item.itemType !== ItemTypeEnum.Version),
        expanded: false,
        isCanBeExpand: item.isCanBeExpand,
        isCanBeQuickRestored: data.isCanBeQuickRestored || (!someParentIsSQL && this.quickRestoreTypes.includes(item.itemType)),
        restorePointObjectPath: data.restorePointObjectPath,
        path: item.path,
        sessionId: root?.sessionId,
        historySessionId: item?.sessionId,
        isRemote: root?.isRemote,
        isAbstract: root?.isAbstract,
        itemType: item.itemType,
        originalSize: item.originalSize,
        modifyDateUTC: item.modifyDateUTC,
        restorePath: item.restorePath,
        isLegacy,
        isDeleted: !item.isPlanExists,
        isCanBeDeleted: item.isCanBeDeleted && !someParentIsSQL,
        needShowRV: root.needShowRV || (item.isNBF && item.type === 'DiskImage'),
        generationId: root.generationId || item.generationId || '',
        restorePoint: root.restorePoint || itemData.restorePoint || '',
        isEncrypted: this.getStorageEncryptionStateByParent(item.isEncrypted, isLegacy || isDeep ? root.isEncrypted : 0),
        isObjectLock: item.isImmutable,
        encryptionPasswordHint: item.encryptionPasswordHint || root.encryptionPasswordHint || '',
        bunchId: root.bunchId || item.bunchId,
        storageId
      } as MyTreeElements;

      const additionalData: Partial<MyTreeElements> = {
        iconCustom: item.itemType === ItemTypeEnum.Bunch ? this.getBunchItemIcon(item) : itemData.src,
        leftTopIcon: itemData.leftTopIcon,
        rightBottomIcon: itemData.rightBottomIcon
      };

      if (item.gfsType) additionalData.GFSSettings = { type: item.gfsType, gfsPurgeSummary: item.gfsPurgeSummary };

      if (item.itemType === ItemTypeEnum.Bunch) {
        this.updateBunchesTreeItem(additionalData, item, root);
      }

      if (item.itemType === ItemTypeEnum.Generation) {
        this.updateGenerationTreeItem(additionalData, item, root);
      }

      if (isRestorePoints) {
        this.updateRestorePointTreeItem(additionalData, item);
      }

      return { ...treeElement, ...additionalData };
    });
  }

  isLegacy(item, root: MyTreeElements, keys: string[]): boolean {
    const isBunch = item.itemType === ItemTypeEnum.Bunch;
    return (
      (item.isNBF === false ||
        ((isBunch || root.itemType === ItemTypeEnum.Prefix) &&
          isNil(item.isNBF) &&
          keys.some((key) => IdsForCurrentFormats[key] === item.displayName))) &&
      isBunch &&
      item.type !== GetBackupContentBunchType.Exchange &&
      item.type !== GetBackupContentBunchType.Database &&
      item.type !== GetBackupContentBunchType.MsSql
    );
  }

  getKeepBackupFor(days: number): string {
    if (!days) return '';
    if (days < 7) return this.i18nPipe.transform('shared:sidepanel-backup.keepBackupFor.days', { param: Math.floor(days) });
    if (days < 30) return this.i18nPipe.transform('shared:sidepanel-backup.keepBackupFor.weeks', { param: Math.floor(days / 7) });
    if (days < 365) return this.i18nPipe.transform('shared:sidepanel-backup.keepBackupFor.months', { param: Math.floor(days / 30) });

    return this.i18nPipe.transform('shared:sidepanel-backup.keepBackupFor.years', { param: Math.floor(days / 365) });
  }

  getStatusText(status: string): string {
    return status === 'fail' || status === 'Fail' ? 'danger' : status?.toLowerCase() || '';
  }

  private updateBaseTreeItem(additionalData: Partial<MyTreeElements>, item: any, root?: MyTreeElements): void {
    additionalData.sizeOnStorage = item.sizeOnStorage;
    additionalData.daysToPurge = item.daysToPurge;
    additionalData.totalSavings = item.totalSavings ? +item.totalSavings.toFixed(2) : item.totalSavings;
    additionalData.purgeSummary = item.purgeSummary;
  }

  private updateBunchesTreeItem(additionalData: Partial<MyTreeElements>, item: any, root?: MyTreeElements): void {
    additionalData.firstRestoreDate = item.firstRestoreDate;
    additionalData.lastRestoreDate = item.lastRestoreDate;
    additionalData.keepBackupFor = this.getKeepBackupFor(item.retentionKeepBackupFor);
    this.updateBaseTreeItem(additionalData, item, root);
    additionalData.countOfErrorStarts = item.countOfErrorRestorePoints || 0;
    additionalData.countOfSuccessfulStarts = item.countOfSuccessfulRestorePoints || 0;
    additionalData.countOfWarningStarts = item.countOfWarningRestorePoints || 0;
    additionalData.type = item.type;
  }

  private updateGenerationTreeItem(additionalData: Partial<MyTreeElements>, item: any, root?: MyTreeElements): void {
    additionalData.firstRestoreDate = item.firstRestoreDate;
    additionalData.lastRestoreDate = item.lastRestoreDate;
    additionalData.tablePurgeSummary = item.tablePurgeSummary || '0';
    this.updateBaseTreeItem(additionalData, item, root);
    additionalData.countOfErrorStarts = item.countOfErrorStarts || 0;
    additionalData.countOfSuccessfulStarts = item.countOfSuccessfulStarts || 0;
    additionalData.countOfWarningStarts = item.countOfWarningStarts || 0;
  }

  private updateRestorePointTreeItem(additionalData: Partial<MyTreeElements>, item: any): void {
    additionalData.date = item.date;
    additionalData.backupResult = this.getStatusText(item?.backupResult);
    additionalData.consistencyCheckResult = this.getStatusText(item?.consistencyCheckResult);
    additionalData.restoreVerificationResult = this.getStatusText(item?.restoreVerificationResult);
    additionalData.sizeOnStorage = item.sizeOnStorage;
    additionalData.totalSavings = item.totalSavings ? +item.totalSavings.toFixed(2) : item.totalSavings;
    additionalData.compressionRatio = item.compressionRatio;
    additionalData.deduplicationRatio = item.deduplicationRatio;
    additionalData.duration = item.duration;
  }

  private getDataFromItemType(item: any, root?: MyTreeElements): DataFromItemType {
    const data: DataFromItemType = {
      src: TreeRestorePointsIconPath.File,
      label:
        item.displayName && item.displayName.length === 2 && item.displayName[item.displayName.length - 1] === ':'
          ? item.displayName + this.joinForInput
          : item.displayName || 'Unnamed',
      leftTopIcon: '',
      rightBottomIcon: ''
    };
    const displayNameArr = item.type === GetBackupContentType.Volume ? item.displayName.split(' ') : [];

    switch (item.itemType) {
      case ItemTypeEnum.DatabaseSql:
        data.src = TreeRestorePointsIconPath.DBPlan;
        break;
      case ItemTypeEnum.Container:
        data.src = TreeBunchesIcons.ProxmoxContainer;
        break;
      case ItemTypeEnum.DatabaseExchange:
        data.rightBottomIcon = TreeBunchesIcons.Exchange;
        break;
      case ItemTypeEnum.Disk:
        data.src = TreeRestorePointsIconPath.ParentDisk;
        break;
      case ItemTypeEnum.Volume:
        data.label = displayNameArr.length > 5 ? displayNameArr.slice(displayNameArr.length - 5).join(' ') : item.displayName;
        data.src = TreeRestorePointsIconPath.Disk;
        break;
      case ItemTypeEnum.Host:
        data.src = TreeRestorePointsIconPath.ServerGroup;
        break;
      case ItemTypeEnum.Machine:
        data.src = this.getIconForMachineLvl(root);
        break;
      case ItemTypeEnum.RestorePoint:
      case ItemTypeEnum.RestorePointIncremental:
      case ItemTypeEnum.RestorePointTLog:
        data.label = moment(item.date).utcOffset(this.providerTimezone).format(mediumDateWithTimeMoment);
        data.restorePoint = StepsHelpers.getNormalDate(item.date);
        data.src = item.isFull
          ? TreeRestorePointsIconPath.Full
          : item.itemType === ItemTypeEnum.RestorePointTLog
            ? TreeRestorePointsIconPath.File
            : TreeRestorePointsIconPath.Diff;
        data.leftTopIcon = this.getLeftTopIcon(item);
        data.rightBottomIcon = this.getRightBottomIcon(item);
        break;
      case ItemTypeEnum.Generation:
        data.src = TreeRestorePointsIconPath.Chain;
        data.leftTopIcon = this.getLeftTopIcon(item);
        data.rightBottomIcon = this.getRightBottomIcon(item);
        data.label = `${moment(item.firstRestoreDate).utcOffset(this.providerTimezone).format(mediumDateWithTimeMoment)} - ${moment(
          item.lastRestoreDate
        )
          .utcOffset(this.providerTimezone)
          .format(mediumDateWithTimeMoment)}`;
        break;
      case ItemTypeEnum.Folder:
        data.src = TreeIconPath.Folder;
        break;
      case ItemTypeEnum.Bunch:
        data.src = TreeIconPath.Folder;
        data.leftTopIcon = this.getLeftTopIcon(item);
        data.rightBottomIcon = this.getBunchLastStatusIcon(item.lastBackupStatus);
        break;
    }

    return data;
  }

  getSourceComputer(item: MyTreeElements, defaultComputer: Computer): Computer | null {
    if (!item?.isRemote) return defaultComputer;

    if (!item) return null;
    if (item.computer) return item.computer;

    return this.getSourceComputer(item.parent, defaultComputer);
  }

  getBunchItem(item: MyTreeElements): MyTreeElements {
    if (!item) return null;
    if (item.itemType === ItemTypeEnum.Bunch) return item;

    return this.getBunchItem(item.parent);
  }

  getIconForMachineLvl(root: MyTreeElements): string {
    const bunch = this.getBunchItem(root);

    if (bunch?.type === GetBackupContentBunchType.Proxmox) return TreeBunchesIcons.Proxmox;
    if (bunch?.type === GetBackupContentBunchType.HyperV) return TreeBunchesIcons.HyperV;
    if (bunch?.type === GetBackupContentBunchType.VMware) return TreeBunchesIcons.VmWare;

    return TreeRestorePointsIconPath.Server;
  }

  getBunchLastStatusIcon(status: ArchiveRestorePointSummaryStatus): string {
    if (status === ArchiveRestorePointSummaryStatus.Fail) return TreeRestorePointsIconPath.Error;
    if (status === ArchiveRestorePointSummaryStatus.Warning) return TreeRestorePointsIconPath.WarnTriangle;
    if (status === ArchiveRestorePointSummaryStatus.Success || status === ArchiveRestorePointSummaryStatus.Running) {
      return TreeRestorePointsIconPath.Success;
    }

    return '';
  }

  public getStorageEncryptionStateByParent(isEncrypted, parentIsEncrypted: StorageEncryptionState): StorageEncryptionState {
    if (isEncrypted === true || parentIsEncrypted === StorageEncryptionState.Encrypted) {
      return StorageEncryptionState.Encrypted;
    }
    if (isEncrypted === false) return StorageEncryptionState.NotEncrypted;

    return isNil(parentIsEncrypted) ? StorageEncryptionState.PossiblyEncrypted : parentIsEncrypted;
  }

  public getBunchItemIcon(item): string {
    if (item.type === GetBackupContentBunchType.DiskImage) return TreeBunchesIcons.Ibb;
    if (item.type === GetBackupContentBunchType.Exchange) return TreeBunchesIcons.Exchange;
    if (item.type === GetBackupContentBunchType.Proxmox) return TreeBunchesIcons.Proxmox;
    if (item.type === GetBackupContentBunchType.Database || item.type === GetBackupContentBunchType.MsSql) return TreeBunchesIcons.Database;

    if (item.type === GetBackupContentBunchType.VMware) {
      return item.bunchName === IdsForCurrentFormats.VMWare ? TreeBunchesIcons.VmWareCurrent : TreeBunchesIcons.VmWare;
    }

    return item.type === GetBackupContentBunchType.HyperV ? TreeBunchesIcons.HyperV : TreeIconPath.Folder;
  }

  private getLeftTopIcon(item: any): string {
    return item.needDeepSync ? TreeRestorePointsIconPath.Quest : item.isEncrypted ? TreeRestorePointsIconPath.Encrypt : '';
  }

  private getRightBottomIcon(item: any): string {
    const warning = item.needDeepSync || item.backupResult === 'Warning' || item.restoreVerificationResult === 'Warning';
    const error = item.backupResult === 'Fail' || item.consistencyCheckResult === 'Fail';
    const key = item.backupResult ?? 'None';

    if (error) return TreeRestorePointsIconPath.Error;
    if (warning) return TreeRestorePointsIconPath.WarnTriangle;

    return TreeRestorePointsIconPath[key] || '';
  }
}
